import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, Calendar } from 'lucide-react';
import { Alert } from '../components/atoms/Alert';
import ReservationForm, { ReservationFormValues } from '../components/molecules/ReservationForm';
import { getImageUrl } from '../utils/imageUrl';
import { formatCurrency } from '../utils/helpers';
import { useAuth } from '../hooks/useAuth';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { buildCreateReservationDto } from '../utils/reservation';
import { useReservationActions } from '@hooks/useReservationActions';

interface LocationState {
  equipment: {
    id: string;
    name: string;
    price: number;
    images: string[];
  };
  dates: {
    startDate: Date;
    endDate: Date;
  };
  totalPrice: number;
}

const ReservationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { equipmentId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const { user, isAuthenticated, signup, login } = useAuth();
  const { createReservation, checkAvailability } = useReservationActions();
  const { handleError, clearError, showSuccess, showWarning, error } = useErrorHandler();

  const state = location.state as LocationState;


  if (!state || !state.equipment || !state.dates) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gray-50">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Reserva Inválida</h2>
          <p className="mb-4 text-gray-600">Esta reserva parece ser inválida o ha expirado.</p>
          <button
            onClick={() => navigate(`/equipment/${equipmentId}`)}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Volver al Equipo
          </button>
        </div>
      </div>
    );
  }

  const { equipment, dates, totalPrice } = state;
  const serviceFee = Math.round(totalPrice * 0.1);
  const totalWithFees = totalPrice + serviceFee;

  // Reservation creation will be handled from the form submit values

  const handleFormSubmit = async (values: ReservationFormValues) => {
    clearError();
    setIsLoading(true);
    try {
      // Si el usuario no está autenticado y quiere registrarse
      if (!isAuthenticated && values.showSignup) {
        if ((values.password ?? '') !== (values.confirmPassword ?? '')) {
          showWarning('Las contraseñas no coinciden');
          setIsLoading(false);
          return;
        }

        if (!values.firstName || !values.lastName || !values.email || !values.phone || !values.shippingAddress || !values.password) {
          showWarning('Por favor, completa todos los campos');
          setIsLoading(false);
          return;
        }

        // Registrar usuario
        await signup(`${values.firstName} ${values.lastName}`, values.email, values.password ?? '');

        // pequeño delay para simular actualización
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      // Construir DTO
      const dto = buildCreateReservationDto({
        equipmentId: equipment.id,
        startDate: dates.startDate,
        endDate: dates.endDate,
        shippingAddress: values.shippingAddress,
        province: values.province,
        city: values.city,
        cp: values.cp,
        user,
        notes: null,
        insuranceIncluded: true,
      });

      // Verificar disponibilidad
      const avail = await checkAvailability(dto.equipmentId, dto.startDate, dto.endDate);
      if (!avail || !avail.available) {
        showWarning('El equipo no está disponible en las fechas seleccionadas. Por favor elige otras fechas.');
        setIsLoading(false);
        return;
      }

      // Crear reserva
      const reservation = await createReservation(dto);
      showSuccess('¡Tu reserva ha sido confirmada!');
      navigate('/confirmation', {
        state: {
          success: true,
          message: '¡Tu reserva ha sido confirmada!',
          bookingId: reservation.reservationCode,
          reservation,
        },
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const daysDifference = Math.ceil((dates.endDate.getTime() - dates.startDate.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen py-6 bg-gray-50">
      <div className="container px-4 mx-auto">
        <div className="max-w-6xl mx-auto">
          <h1 className="mb-4 text-2xl font-bold text-gray-900">Confirmar y Pagar</h1>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Formulario de Reserva */}
            <div className="md:col-span-2">
              <div className="p-6 mb-6 bg-white shadow-sm rounded-xl">
                {/* Reserva form moved to component for clarity */}
                {error && (
                  <div className="container mx-auto my-4">
                    <Alert
                      type={error.type}
                      message={error.message}
                      onClose={() => clearError()}
                    />
                  </div>
                )}
                <ReservationForm onSubmit={handleFormSubmit} isLoading={isLoading} isAuthenticated={isAuthenticated} login={login} onError={handleError} />
                {error && (
                  <div className="container mx-auto my-4">
                    <Alert
                      type={error.type}
                      message={error.message}
                      onClose={() => clearError()}
                    />
                  </div>
                )}
              </div>

              {/* Protección de Alquiler */}
              <div className="p-6 border border-blue-100 bg-blue-50 rounded-xl">
                <div className="flex items-start">
                  <Shield className="flex-shrink-0 mr-4 text-blue-600" size={24} />
                  <div>
                    <h3 className="mb-2 font-medium text-gray-900">Protección de Alquiler</h3>
                    <p className="text-sm text-gray-600">
                      Tu alquiler está protegido por nuestra póliza de seguro integral, que cubre daños accidentales y responsabilidad civil hasta {formatCurrency(10000)}.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Resumen de Precio */}
            <div className="md:col-span-1">
              <div className="sticky p-6 bg-white shadow-sm rounded-xl top-24">
                <h2 className="mb-4 text-xl font-semibold">Tu Reserva</h2>

                <div className="flex items-start mb-6 space-x-4">
                  <img
                    src={getImageUrl(equipment.images[0])}
                    alt={equipment.name}
                    className="object-cover w-24 h-24 rounded-lg"
                  />
                  <div>
                    <h3 className="font-medium text-gray-900">{equipment.name}</h3>
                    <div className="mt-1 text-sm text-gray-500">
                      <div>Inicio: {dates.startDate.toLocaleDateString('es-ES')}</div>
                      <div>Fin: {dates.endDate.toLocaleDateString('es-ES')}</div>
                    </div>
                  </div>
                </div>
                <h3 className="mb-4 text-lg font-medium">Detalles del Precio</h3>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{formatCurrency(equipment.price, 'EUR', 'es-ES', true)} × {daysDifference} días</span>
                    <span>{formatCurrency(totalPrice, 'EUR', 'es-ES', true)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tarifa de servicio</span>
                    <span>{formatCurrency(serviceFee, 'EUR', 'es-ES', true)}</span>
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between font-medium">
                      <span>Total</span>
                      <span>{formatCurrency(totalWithFees, 'EUR', 'es-ES', true)}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 text-sm text-gray-500">
                  <div className="flex items-center mb-2">
                    <Calendar size={16} className="mr-2" />
                    <span>Cancelación gratuita hasta 24 horas antes de la recogida</span>
                  </div>
                  <div className="flex items-center">
                    <Shield size={16} className="mr-2" />
                    <span>Protección de alquiler incluida</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationPage;
